/**
 * Jitter Lottie pair:
 * 1) Looping hero with scroll-driven focal zoom/pan (left → center → right → reset)
 * 2) Second Lottie scrubs on scroll while first stays sticky; both exit together
 */

import lottie from 'lottie-web/build/player/lottie_light';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/** Same focal map as hero banner in home.js */
const SCALE = 2;
const MOVE = 0.8;
const PAUSE = 0.2;
const SCRUB_MOVE = 6;

const focals = [
	{ scale: 1, x: '0%', y: '0%' },
	{ scale: SCALE, x: '50%', y: '-50%' },
	{ scale: SCALE, x: '-1%', y: '-26%' },
	{ scale: SCALE, x: '-50%', y: '-50%' },
];

/** SVG slice + top align = background-size: cover; background-position: top center */
const lottieCoverSettings = {
	preserveAspectRatio: 'xMidYMin slice',
};

/**
 * @returns {void | (() => void)}
 */
export function initJitterScrollSection() {
	const group = document.querySelector('.jitter-scroll-group');
	const loopSection = group?.querySelector('.jitter-scroll-section--loop');
	const cardsSection = group?.querySelector('.jitter-scroll-section--cards');
	const scrubSection = group?.querySelector('.jitter-scroll-section--scrub');
	const loopMotion = loopSection?.querySelector('.jitter-scroll-section__animation');
	const cardsMotion = cardsSection?.querySelector('.jitter-scroll-section__motion');
	const scrubContainer = scrubSection?.querySelector('.jitter-scroll-section__animation');
	const highlightsTab = group?.querySelector('.hero-recent-highlights-tab');
	const scrollDownBtn = group?.querySelector('.hero-scroll-down-btn');

	if (!group || !loopSection || !scrubSection || !loopMotion || !scrubContainer) {
		return undefined;
	}

	const panTargets = cardsMotion ? [loopMotion, cardsMotion] : [loopMotion];
	const focalCards = cardsMotion
		? /** @type {HTMLElement[]} */ ([...cardsMotion.querySelectorAll('.jitter-focal-card')])
		: [];

	/** @type {Set<string>} */
	const activatedSlots = new Set();

	/** One-shot: once a card is active it stays active. */
	const activateCard = (/** @type {'right' | 'center' | 'left'} */ slot) => {
		if (activatedSlots.has(slot)) {
			return;
		}
		activatedSlots.add(slot);
		focalCards.forEach((card) => {
			if (card.getAttribute('data-focal') === slot) {
				card.classList.add('is-active');
			}
		});
	};

	const resetCards = () => {
		activatedSlots.clear();
		focalCards.forEach((card) => {
			card.classList.remove('is-active');
		});
	};

	const scrollThreshold = 0;

	const setScrollDownBtnVisible = (/** @type {boolean} */ visible) => {
		if (!scrollDownBtn) {
			return;
		}
		scrollDownBtn.classList.toggle('is-hidden', !visible);
		scrollDownBtn.setAttribute('aria-hidden', visible ? 'false' : 'true');
	};

	const updateScrollDownBtn = () => {
		setScrollDownBtnVisible(window.scrollY <= scrollThreshold);
	};

	const bindScrollDownBtn = () => {
		if (!scrollDownBtn) {
			return;
		}

		setScrollDownBtnVisible(window.scrollY <= scrollThreshold);
		window.addEventListener('scroll', updateScrollDownBtn, { passive: true });

		scrollDownBtn.addEventListener('click', () => {
			window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
		});
	};

	bindScrollDownBtn();

	const loopUrl = loopSection.getAttribute('data-lottie-src');
	const scrubUrl = scrubSection.getAttribute('data-lottie-src');
	if (!loopUrl || !scrubUrl) {
		console.warn('[JitterScroll] Missing data-lottie-src on loop and/or scrub blocks');
		return undefined;
	}

	gsap.registerPlugin(ScrollTrigger);

	/** @type {import('lottie-web').AnimationItem | null} */
	let loopAnim = null;
	/** @type {import('lottie-web').AnimationItem | null} */
	let scrubAnim = null;
	/** @type {import('gsap/core').Timeline | null} */
	let masterTl = null;

	/** @type {number} */
	let scrollIdleTimer = 0;
	let isScrolling = false;
	let loopPausedForScrub = false;

	const isInScrubPhase = () => {
		if (!masterTl || masterTl.labels.scrubPhase === undefined) {
			return false;
		}
		return masterTl.time() >= masterTl.labels.scrubPhase;
	};

	const syncLoopPlayback = () => {
		if (!loopAnim) {
			return;
		}

		const inScrubPhase = isInScrubPhase();
		if (inScrubPhase) {
			if (!loopPausedForScrub) {
				loopPausedForScrub = true;
				loopAnim.pause();
			}
			return;
		}

		if (loopPausedForScrub) {
			loopPausedForScrub = false;
		}
		if (!isScrolling) {
			loopAnim.play();
		}
	};

	const onScrollActive = () => {
		if (!isScrolling) {
			isScrolling = true;
			if (loopAnim && !isInScrubPhase()) {
				loopAnim.pause();
			}
			panTargets.forEach((el) => {
				/** @type {HTMLElement} */ (el).style.willChange = 'transform';
			});
		}
		clearTimeout(scrollIdleTimer);
		scrollIdleTimer = window.setTimeout(() => {
			isScrolling = false;
			syncLoopPlayback();
			panTargets.forEach((el) => {
				/** @type {HTMLElement} */ (el).style.willChange = '';
			});
		}, 150);
	};

	const fetchJson = async (/** @type {string} */ url) => {
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(String(response.status));
		}
		return response.json();
	};

	const load = async () => {
		let loopData;
		let scrubData;
		try {
			[loopData, scrubData] = await Promise.all([fetchJson(loopUrl), fetchJson(scrubUrl)]);
		} catch (err) {
			console.warn('[JitterScroll] Fetch failed', err);
			return;
		}

		loopAnim = lottie.loadAnimation({
			container: loopMotion,
			renderer: 'svg',
			loop: true,
			autoplay: true,
			animationData: loopData,
			rendererSettings: lottieCoverSettings,
		});

		scrubAnim = lottie.loadAnimation({
			container: scrubContainer,
			renderer: 'svg',
			loop: false,
			autoplay: false,
			animationData: scrubData,
			rendererSettings: lottieCoverSettings,
		});

		const setupTimeline = () => {
			if (!scrubAnim) {
				return;
			}

			const lastFrame = Math.max(scrubAnim.totalFrames - 1, 0);
			const scrubProxy = { frame: 0 };

			gsap.set(panTargets, { scale: 1, x: '0%', y: '0%', transformOrigin: '50% 50%' });
			resetCards();
			gsap.set(scrubSection, { opacity: 0, visibility: 'hidden' });
			if (highlightsTab) {
				gsap.set(highlightsTab, { autoAlpha: 1, pointerEvents: 'auto' });
			}

			masterTl = gsap.timeline({
				scrollTrigger: {
					trigger: group,
					start: 'top top',
					end: 'bottom bottom',
					scrub: 1,
					onUpdate() {
						onScrollActive();
						syncLoopPlayback();
					},
				},
			});

			// Zoom / pan: full → right → center → left → reset (hero + cards layers move together)
			// Absolute positions in the timeline:
			//   0       → MOVE                      pan to right
			//   MOVE    → MOVE + PAUSE               pause at right
			//   M+P     → 2M + P                     pan to center
			//   2M+P    → 2M + 2P                    pause at center
			//   2M+2P   → 3M + 2P                    pan to left
			//   3M+2P   → 3M + 3P                    pause at left
			//   3M+3P   → 4M + 3P                    pan to reset
			const rightPanStart = 0;
			const centerPanStart = MOVE + PAUSE;
			const leftPanStart = 2 * MOVE + 2 * PAUSE;

			masterTl.addLabel('panRight', rightPanStart);
			masterTl.to(panTargets, { scale: focals[1].scale, x: focals[1].x, y: focals[1].y, duration: MOVE, ease: 'power2.inOut' });
			masterTl.to(panTargets, { duration: PAUSE });

			masterTl.addLabel('panCenter', centerPanStart);
			masterTl.to(panTargets, { scale: focals[2].scale, x: focals[2].x, y: focals[2].y, duration: MOVE, ease: 'power2.inOut' });
			masterTl.to(panTargets, { duration: PAUSE });

			masterTl.addLabel('panLeft', leftPanStart);
			masterTl.to(panTargets, { scale: focals[3].scale, x: focals[3].x, y: focals[3].y, duration: MOVE, ease: 'power2.inOut' });
			masterTl.to(panTargets, { duration: PAUSE });

			masterTl.to(panTargets, { scale: focals[0].scale, x: focals[0].x, y: focals[0].y, duration: MOVE, ease: 'power2.inOut' });

			masterTl.addLabel('scrubPhase');

			// Activate cards at 50% through each pan — CSS transition handles the ease-in
			masterTl.call(() => activateCard('left'), null, rightPanStart + MOVE * 0.5);
			masterTl.call(() => activateCard('center'), null, centerPanStart + MOVE * 0.5);
			masterTl.call(() => activateCard('right'), null, leftPanStart + MOVE * 0.5);

			// Second Lottie: reveal and scrub; hide highlights tab for this phase
			if (highlightsTab) {
				masterTl.to(highlightsTab, {
					autoAlpha: 0,
					pointerEvents: 'none',
					duration: 0.01,
					onComplete() {
						highlightsTab.setAttribute('aria-hidden', 'true');
					},
					onReverseComplete() {
						highlightsTab.removeAttribute('aria-hidden');
					},
				});
			}
			masterTl.to(scrubSection, {
				opacity: 1,
				visibility: 'visible',
				duration: 0.01,
			}, highlightsTab ? '<' : undefined);
			masterTl.to(
				scrubProxy,
				{
					frame: lastFrame,
					duration: SCRUB_MOVE,
					ease: 'none',
					onUpdate() {
						if (scrubAnim) {
							scrubAnim.goToAndStop(scrubProxy.frame, true);
						}
					},
				},
				'<',
			);

			syncLoopPlayback();
			ScrollTrigger.refresh();
		};

		if (scrubAnim.isLoaded) {
			setupTimeline();
		} else {
			scrubAnim.addEventListener('DOMLoaded', setupTimeline);
		}
	};

	load();

	return () => {
		clearTimeout(scrollIdleTimer);
		isScrolling = false;
		loopPausedForScrub = false;
		window.removeEventListener('scroll', updateScrollDownBtn);
		masterTl?.scrollTrigger?.kill();
		masterTl?.kill();
		masterTl = null;
		if (loopAnim) {
			loopAnim.destroy();
			loopAnim = null;
		}
		if (scrubAnim) {
			scrubAnim.destroy();
			scrubAnim = null;
		}
		gsap.set(loopMotion, { clearProps: 'all' });
		/** @type {HTMLElement} */ (loopMotion).style.willChange = '';
		if (cardsMotion) {
			gsap.set(cardsMotion, { clearProps: 'all' });
			/** @type {HTMLElement} */ (cardsMotion).style.willChange = '';
		}
		resetCards();
		gsap.set(scrubSection, { clearProps: 'all' });
		if (highlightsTab) {
			highlightsTab.removeAttribute('aria-hidden');
			gsap.set(highlightsTab, { clearProps: 'all' });
		}
		if (scrollDownBtn) {
			scrollDownBtn.classList.remove('is-hidden');
			scrollDownBtn.removeAttribute('aria-hidden');
		}
	};
};
